import { useEffect, useRef, useState } from 'react';

type CopyFn = (text: string, container?: HTMLElement | null) => Promise<boolean>;

// The async Clipboard API requires a secure context and a delegated clipboard-write
// permission, neither of which is guaranteed inside the builder preview iframe (where
// navigator.clipboard is often undefined). Fall back to the legacy execCommand path,
// which works in those contexts off the user's click gesture.
const writeToClipboard = async (text: string, container?: HTMLElement | null): Promise<boolean> => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the execCommand fallback below.
    }
  }

  try {
    const parent = container ?? document.body;
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    parent.appendChild(textarea);
    try {
      textarea.select();
      return document.execCommand('copy');
    } finally {
      // Guarantee the textarea is removed even if select()/execCommand throws.
      parent.removeChild(textarea);
    }
  } catch {
    return false;
  }
};

/**
 * Copy text to the clipboard, tracking a transient `copied` flag that flips back to
 * false after `resetDelay` ms. Falls back to execCommand when the async Clipboard API
 * is unavailable (e.g. inside the builder preview iframe).
 *
 * ```tsx
 * const { copied, copy } = useCopyToClipboard();
 * <button type="button" onClick={() => copy(code)}>{copied ? 'Copied' : 'Copy'}</button>
 * ```
 */
export const useCopyToClipboard = ({ resetDelay = 2000 }: { resetDelay?: number } = {}) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const copy: CopyFn = async (text, container) => {
    const succeeded = await writeToClipboard(text, container);
    if (!succeeded) return false;

    setCopied(true);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCopied(false), resetDelay);
    return true;
  };

  return { copied, copy };
};
